import React from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import { partnersData } from '../../data/partnersData.ts'; // Import from new location with extension

// --- START: Copied/Adapted from MerchantWalletDashboard ---

// Currency mapping (CODE) - Assuming base amounts are USD, this maps location to display currency code
const currencyMap: { [key: string]: string } = {
    'UAE': 'AED',
    'KSA': 'SAR',
    'Qatar': 'QAR',
    'Kuwait': 'KWD',
    'Bahrain': 'BHD',
    'Egypt': 'EGP', // Added Egypt
    'Oman': 'OMR',  // Added Oman
};

// Currency SYMBOL mapping - Assuming base amounts are USD, this maps location to display currency symbol
const currencySymbolMap: { [key: string]: string } = {
    'UAE': 'AED',
    'KSA': 'SAR',
    'Qatar': 'QAR',
    'Kuwait': 'KWD',
    'Bahrain': 'BHD',
    'Egypt': 'EGP', // Added Egypt
    'Oman': 'OMR',  // Added Oman
    'All': '$', // Symbol for 'All' countries (or if location unknown)
};

// Helper to format amount with currency symbol and adjust based on country
// **Note**: The baseAmount is assumed to be in a base currency (e.g., USD).
// The factor converts it to the local currency for display.
// The 'All' country type will display in the base currency symbol ($).
const formatCurrency = (baseAmount: number, country: string): string => {
    const symbol = currencySymbolMap[country] || '$'; // Default to '$' if country not in symbol map or is 'All'

    // Simple adjustment factor for demonstration - **Assume baseAmount is USD**
    let factor = 1;
    // Add more plausible exchange rates (approximate)
    switch (country) {
        case 'KSA': factor = 3.75; break; // SAR
        case 'Qatar': factor = 3.64; break; // QAR
        case 'Kuwait': factor = 0.31; break; // KWD (Note: 1 KWD > 1 USD)
        case 'Bahrain': factor = 0.38; break; // BHD (Note: 1 BHD > 1 USD)
        case 'UAE': factor = 3.67; break;  // AED
        case 'Egypt': factor = 47.5; break; // EGP (Approx)
        case 'Oman': factor = 0.38; break;  // OMR (Approx)
        default: factor = 1; break; // Default factor (USD)
    }

    // Apply factor only if NOT 'All' (although 'All' isn't used for individual partner location)
    const adjustedAmount = baseAmount * factor;
    // Format with symbol, commas, and round to nearest integer (no decimals)
    return `${symbol} ${Math.round(adjustedAmount).toLocaleString()}`;
};


// Helper component for Status Badge (can be moved to a shared components folder later)
const StatusBadge: React.FC<{ status: 'Active' | 'Inactive' | 'Pending' }> = ({ status }) => {
  let backgroundColor = '';
  let color = '';
  // Updated badge colors for lighter theme
  switch (status) {
    case 'Active': backgroundColor = '#ecfdf5'; color = '#059669'; break; // Lighter Green
    case 'Inactive': backgroundColor = '#f8fafc'; color = '#64748b'; break; // Lighter Gray
    case 'Pending': backgroundColor = '#fefce8'; color = '#d97706'; break; // Lighter Yellow/Amber
  }
  return <span style={{ backgroundColor, color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>{status}</span>;
};

// Helper component for Dropdown Filter (can be moved)
interface DropdownFilterProps {
    label: string;
    options: string[];
    selectedValue: string;
    onChange: (value: string) => void;
}

const DropdownFilter: React.FC<DropdownFilterProps> = ({ label, options, selectedValue, onChange }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [hoveredOption, setHoveredOption] = React.useState<string | null>(null); // State for hover
    const dropdownRef = React.useRef<HTMLDivElement>(null); // Ref for the dropdown container

    const handleSelect = (option: string) => {
        onChange(option);
        setIsOpen(false);
    };

    // Effect to handle clicks outside the dropdown
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]); // Re-run effect when isOpen changes

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}> {/* Attach ref here */}
            <button
                style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db', // Keep light gray border
                    backgroundColor: 'white',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    minWidth: '140px',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out' // Add transition
                }}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#adb5bd'} // Slightly darken border on hover
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'} // Restore border color
            >
                {label}: <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedValue}</span>
                {/* Custom Arrow */}
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? "m18 14-6-6-6 6" : "m6 10 6 6 6-6"} />
                </svg>
            </button>
            {isOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '160px', padding: '4px 0' }}> {/* Adjusted shadow and margin */}
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {options.map(option => (
                            <li
                                key={option}
                                style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    backgroundColor: hoveredOption === option ? '#f9fafb' : 'white' // Lighter hover background
                                }}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => setHoveredOption(option)} // Set hover state
                                onMouseLeave={() => setHoveredOption(null)}   // Clear hover state
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// --- END: Copied/Adapted from MerchantWalletDashboard ---


const PartnersPage: React.FC = () => {
  const navigate = useNavigate();

  // State for filters and actions dropdown
  const [selectedProduct, setSelectedProduct] = React.useState<string>('All Products');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('All');
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<string | number | null>(null);

  // Filter partners based only on selected product and country
  const filteredPartners = React.useMemo(() => {
    return partnersData.filter(partner => {
        const productMatch = selectedProduct === 'All Products' || partner.products.includes(selectedProduct);
        const countryMatch = selectedCountry === 'All' || partner.location === selectedCountry;

        return productMatch && countryMatch;
    });
  }, [selectedProduct, selectedCountry]);

  // NEW: Handle partner row click (Navigate to a potential partner detail page)
  const handlePartnerClick = (partnerName: string) => {
      const partnerId = partnerName.toLowerCase().replace(/\s+/g, '-'); // Simple slug generation
      console.log(`Navigate to profile for: ${partnerName} (ID: ${partnerId})`);
      // Navigate to partner details page
      navigate(`/partners/${partnerId}`);
  };


  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        {/* Header Area */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600', color: '#111827', lineHeight: '1.2' }}>Partners</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>View and manage your partnerships.</p>
          </div>
           {/* Filter Row */}
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              {/* Left Side: Filters */}
              <div style={{ display: 'flex', gap: '12px' }}>
                  {/* Country Dropdown Filter */}
                  <DropdownFilter
                      label="Country"
                      options={['All', 'UAE', 'KSA', 'Kuwait', 'Bahrain', 'Qatar']}
                      selectedValue={selectedCountry}
                      onChange={setSelectedCountry}
                  />
                  {/* Product Dropdown Filter */}
                  <DropdownFilter
                      label="Product"
                      options={['All Products', ...['BNPL', 'Acquiring', 'Prepaid Ads', 'Capital', 'Tabby Card', 'Postpaid Ads']]}
                      selectedValue={selectedProduct}
                      onChange={setSelectedProduct}
                  />
              </div>
              {/* Right Side: Add Partner Button (Placeholder) - REMOVED */}
              {/*
              <button
                  style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#059669', // Tabby green
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                  }}
                  onClick={() => alert('Add Partner Clicked (Placeholder)')}
              >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path fillRule="evenodd" d="M7.75 2a.75.75 0 01.75.75V7h4.25a.75.75 0 110 1.5H8.5v4.25a.75.75 0 11-1.5 0V8.5H2.75a.75.75 0 110-1.5H7V2.75A.75.75 0 017.75 2z"></path></svg>
                  Add Partner
              </button>
              */}
          </div>
        </div>

        {/* Main Content Area: Partners Table */}
        <section style={{ marginTop: '24px', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' /* Match TransactionsPage section style */ }}>
            {/* Filter Bar Area - Div with padding and border removed */}
            {/*
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                     <div style={{ flexGrow: 1 }}></div>
                </div>
            </div>
            */}

             {/* Partners Table */}
            {filteredPartners.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                {/* Apply TransactionsPage thead styles */}
                <thead style={{ backgroundColor: '#f9fafb' }}>
                    {/* Apply TransactionsPage header row styles */}
                    <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                        {/* Apply TransactionsPage th styles (padding, weight) */}
                        <th style={{ fontWeight: 500, padding: '8px 24px' }}>Partner</th>
                        <th style={{ fontWeight: 500, padding: '8px 8px' }}>Products</th>
                        <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'right' }}>Debit Balance</th>
                        <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'right' }}>Credit Balance</th>
                        <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'center' }}>Status</th>
                        <th style={{ fontWeight: 500, padding: '8px 8px', textAlign: 'center' }}>Risk Score</th>
                        <th style={{ fontWeight: 500, padding: '8px 8px' }}>Last Payout</th>
                        {/* Actions column - Use specific padding like TransactionsPage last column if needed */}
                        <th style={{ fontWeight: 500, padding: '8px 24px 8px 8px', textAlign: 'center' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredPartners.map((partner) => (
                    // Apply TransactionsPage body row styles
                    <tr
                        key={partner.name}
                        style={{
                            borderBottom: '1px solid #f3f4f6',
                            cursor: 'pointer',
                            backgroundColor: hoveredRowIndex === partner.name ? '#f9fafb' : 'transparent',
                            transition: 'background-color 0.1s ease-in-out'
                        }}
                        onClick={() => handlePartnerClick(partner.name)}
                        onMouseEnter={() => setHoveredRowIndex(partner.name)}
                        onMouseLeave={() => setHoveredRowIndex(null)}
                        >
                        {/* Partner Name & Logo - Apply TransactionsPage first cell padding */}
                        <td style={{ padding: '12px 8px 12px 24px', display: 'flex', alignItems: 'center' }}>
                            {partner.logoUrl ? (
                                <img
                                src={partner.logoUrl}
                                alt={`${partner.name} logo`}
                                style={{ marginRight: '10px', width: '24px', height: '24px', borderRadius: '50%', objectFit: 'contain', border: '1px solid #f3f4f6' }}
                                onError={(e) => {
                                    const imgElement = e.target as HTMLImageElement;
                                    imgElement.style.display = 'none';
                                    const fallbackSpan = imgElement.nextElementSibling as HTMLElement | null;
                                    if (fallbackSpan && fallbackSpan.classList.contains('logo-fallback')) {
                                    fallbackSpan.style.display = 'flex';
                                    fallbackSpan.style.marginRight = '10px';
                                    }
                                }}
                                />
                            ) : null}
                            <span
                                className="logo-fallback"
                                style={{
                                    marginRight: '10px',
                                    display: partner.logoUrl ? 'none' : 'flex',
                                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#f3f4f6',
                                    alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase'
                                }}>
                                {partner.name.split(' ').map(n => n?.[0]).filter(Boolean).slice(0, 2).join('')}
                            </span>
                            <div>
                                <div style={{ fontWeight: 500, color: '#111827' }}>{partner.name}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{partner.location}</div>
                            </div>
                        </td>
                        {/* Products - Apply TransactionsPage standard cell padding */}
                        <td style={{ padding: '12px 8px' }}>
                            {partner.products.map(p => <span key={p} style={{ backgroundColor: '#f3f4f6', color: '#374151', padding: '3px 8px', borderRadius: '4px', marginRight: '5px', fontSize: '12px', display: 'inline-block', marginBottom: '4px' }}>{p}</span>)}
                        </td>
                        {/* Balances - Apply TransactionsPage standard cell padding, keep right align */}
                        <td style={{ padding: '12px 8px', color: '#111827', textAlign: 'right' /* Remove monospace */ }}>{formatCurrency(partner.baseDebit, partner.location)}</td>
                        <td style={{ padding: '12px 8px', color: '#111827', textAlign: 'right' /* Remove monospace */ }}>{formatCurrency(partner.baseCredit, partner.location)}</td>
                        {/* Status - Apply TransactionsPage standard cell padding, keep center align */}
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <StatusBadge status={partner.status as 'Active' | 'Inactive' | 'Pending'} />
                        </td>
                        {/* Risk Score - Apply TransactionsPage standard cell padding, keep center align */}
                        <td style={{ padding: '12px 8px', textAlign: 'center', color: parseInt(partner.risk) >= 90 ? '#059669' : (parseInt(partner.risk) < 70 ? '#f43f5e' : '#4b5563') }}>{partner.risk}</td>
                        {/* Last Payout - Apply TransactionsPage standard cell padding */}
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>{partner.lastPayout}</td>
                        {/* Actions - Apply TransactionsPage last cell padding */}
                        <td style={{ padding: '12px 24px 12px 8px', textAlign: 'center' }}>
                            <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af' }} onClick={(e) => { e.stopPropagation(); alert(`Actions for ${partner.name}`)}}>...</button>
                        </td>
                    </tr>
                    ))}
                </tbody>
                </table>
            ) : (
                // Empty State - Add horizontal padding to match table content
                <div style={{ textAlign: 'center', padding: '40px 24px', color: '#6b7280', borderTop: '1px solid #e5e7eb' /* Keep border */ }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px', color: '#d1d5db' }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><circle cx="11.5" cy="11.5" r="2.5"></circle><line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
                    </svg>
                    <p style={{ fontSize: '14px', fontWeight: '500', margin: 0, color: '#111827' }}>No Partners Found</p>
                    <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>Try adjusting your search or filter criteria.</p>
                </div>
            )}
        </section>
      </div>
    </MainLayout>
  );
};

export default PartnersPage;
